import type {
  BarChartInstrument,
  ChartInstrumentDataSeriesValue,
} from 'lib-analytics-types';
import memoizeOne from 'memoize-one';

/** Represents the Recharts mapping of all chart series and their values. */
type RechartsSeriesValues = Readonly<Record<string, string | number | null>>;

/** Represents the Recharts chart category. */
export type RechartsCategory = {
  /** The name of the category. */
  readonly name: string;
} & RechartsSeriesValues;

/**
 * Transforms the chart instrument data to the format the Recharts library
 * requires, which is an object with the name of the category and key-value
 * pairs of the series and their values.
 */
export const transformDataForRecharts = memoizeOne(
  (
    data: BarChartInstrument['data'],
    options: { omitZero: boolean },
  ): RechartsCategory[] =>
    data.categories.map((category, categoryIndex) => {
      const rechartsSeries: RechartsSeriesValues = data.series.reduce(
        (out, { label, data }) => {
          const { value } = getSeriesValue(data[categoryIndex]);
          const omitValue = options.omitZero ? !value : value === null;
          return omitValue ? out : { ...out, [label]: value };
        },
        {} as RechartsSeriesValues,
      );

      const rechartsCategory: RechartsCategory = {
        name: category.label,
        ...rechartsSeries,
      };

      return rechartsCategory;
    }),
);

/**
 * Returns the value object of a series corresponding to a given category. If
 * the input is a number or null (legacy data types), it is converted to an
 * object with a `value` property for backwards-compatibility. Otherwise it is
 * returned as-is, or null if the input is undefined.
 */
export const getSeriesValue = (
  input?: ChartInstrumentDataSeriesValue | number | null,
): ChartInstrumentDataSeriesValue => {
  const isPrimitive = input === null || typeof input === 'number';

  if (isPrimitive) {
    return { value: input };
  }

  return {
    value: input?.value ?? null,
    metrics: input?.metrics,
  };
};
